import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

import { heroImageSets, HERO_IMAGE_SIZES } from "../Assets/heroImages";
import omokaBannerAsset from "../Assets/Omoka na Zetech banner (Web Slider).png";

import { useHeroSlides } from "../hooks/useHeroSlides";
import Mascot from "../ZETY-MASCOT/Mascot";
import { openTechizenChat } from "../ZETY-MASCOT/chatBus";

// Original hardcoded slides — used as the fallback/default content
// whenever the `hero_slides` table is empty or unreachable, so the
// hero section never breaks or goes blank. Local bundled images are
// matched back onto the Supabase rows by position (display_order).
const FALLBACK_IMAGES = [
  ...heroImageSets.map((set) => set.src),
  omokaBannerAsset,
];

// How long each slide holds the stage. Six seconds is the sweet spot for
// hero advertising: long enough to read a headline and a CTA, short enough
// that the rotation still feels alive.
const SLIDE_DURATION = 6000;

const FALLBACK_SLIDES = [

  {
    image: FALLBACK_IMAGES[0],
    imageSet: heroImageSets[0],
    // badge: "Inventing the Future",
    // title: "Kenya's Leading Technological & Entrepreneurial University",
    // description:
    //   "For over 25 years, Zetech University has empowered learners through innovation, entrepreneurship, research and industry-driven education that prepares graduates for the future of work.",
    badge: "Inventing the Future",
    title: "Where Technology, Innovation & Entrepreneurship Shape the Future",
    description:
    "For over 25 years, Zetech University has empowered a new generation of innovators, entrepreneurs and leaders through technology-driven education, impactful research and strong industry partnerships—preparing graduates to solve real-world challenges and shape the future.",
    primaryButton: "Apply Now",
    primaryLink: "https://sajili.zetech.ac.ke",
    secondaryButton: "Explore Programmes",
    secondaryLink: "/programmes",
    mascotMessage:
      "👋 Hi! I'm Techizen.\nLet's invent the future together!",
  },
  {
    image: FALLBACK_IMAGES[1],
    imageSet: heroImageSets[1],
    badge: "75,000+ Alumni",
    title: "Your Journey to Success Starts Here",
    description:
      "Join a vibrant community of innovators, entrepreneurs and professionals making an impact across Kenya and beyond.",
    primaryButton: "Join Zetech",
    primaryLink: "https://sajili.zetech.ac.ke",
    secondaryButton: "View Courses",
    secondaryLink: "/programmes",
    mascotMessage:
      "🎓 Become part of our 75,000+ alumni family!",
  },
  {
    image: FALLBACK_IMAGES[2],
    imageSet: heroImageSets[2],
    badge: "Admissions Open",
    title: "Experience Campus Life Like Never Before",
    description:
      "Discover world-class facilities, vibrant student life, sports, leadership opportunities and a campus designed for your success.",
    primaryButton: "Student Life",
    primaryLink: "/students-life",
    secondaryButton: "Virtual Tour",
    secondaryLink: "/our-campuses",
    mascotMessage:
      "🚀 Applications are open!\nLet's get started.",
  },
  {
    image: FALLBACK_IMAGES[3],
    imageSet: heroImageSets[3],
    badge: "Top Ranked University",
    title: "Innovation. Research. Excellence.",
    description:
      "Study in an ISO 9001:2015 Certified University recognised for technology, entrepreneurship and blended learning excellence.",
    primaryButton: "Why Zetech",
    primaryLink: "/about-zu",
    secondaryButton: "Contact Us",
    secondaryLink: "mailto:info@zetech.ac.ke?subject=Zetech%20University%20Enquiry",
    mascotMessage:
      "🤖 Ask me anything about Zetech University!",
  },
  {
    image: FALLBACK_IMAGES[4],
    // The promotional banner is a single artwork, not a responsive image set.
    imageSet: null,
    isBanner: true,
    badge: "",
    title: "Omoka na Zetech",
    description: "",
    primaryButton: "Contact Caleb",
    primaryLink: "tel:0768480506",
    // secondaryButton: "View Courses",
    // secondaryLink: "/programmes",
    mascotMessage: "",
  },
];


// Maps a Supabase hero_slides row onto the shape this component renders.
function mapSlide(row, index) {
  const localSet = heroImageSets[index % heroImageSets.length];
  return {
    image: row.image_url || localSet.src,
    // Responsive variants only exist for the bundled artwork; CMS uploads
    // are served as-is.
    imageSet: row.image_url ? null : localSet,
    isBanner: row.is_banner || false,
    badge: row.badge,
    title: row.title,
    description: row.description,
    primaryButton: row.primary_button_text,
    primaryLink: row.primary_button_link,
    secondaryButton: row.secondary_button_text,
    secondaryLink: row.secondary_button_link,
    mascotMessage: row.mascot_message,
  };
}

// Renders a hero CTA. Links added from the admin can be internal paths
// ("/programmes"), absolute URLs ("https://sajili.zetech.ac.ke"),
// mailto:/tel: links or hash anchors — react-router's <Link> only
// handles the first kind, so pick the right element per link type.
function HeroButton({ href, className, label }) {
  if (!label) return null;

  const link = (href || "").trim();

  if (!link) {
    return null;
  }

  const isExternal = /^(https?:)?\/\//i.test(link) ||
    /^(mailto:|tel:)/i.test(link);

  if (isExternal) {
    return (
      <a
        href={link}
        className={className}
        target={/^(mailto:|tel:)/i.test(link) ? undefined : "_blank"}
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }

  if (link.startsWith("#")) {
    return (
      <a href={link} className={className}>
        {label}
      </a>
    );
  }

  return (
    <Link to={link.startsWith("/") ? link : `/${link}`} className={className}>
      {label}
    </Link>
  );
}


export default function HeroSlider({ onCycleEnd, onRequestVideo, initialIndex = 0 }) {
  const { slides: dbSlides, loading, error } = useHeroSlides();

  const slides = useMemo(() => {
    if (!loading && !error && dbSlides.length > 0) {
      return dbSlides.map(mapSlide);
    }
    return FALLBACK_SLIDES;
  }, [dbSlides, loading, error]);

  // `initialIndex === -1` means "open on the last slide" — used when the
  // visitor steps backwards out of the intro video.
  const [current, setCurrent] = useState(() =>
    initialIndex < 0 ? Math.max(slides.length - 1, 0) : initialIndex
  );
  const [paused, setPaused] = useState(false);

  // Respect the OS "reduce motion" setting: no Ken Burns drift, no timer bar
  // animation for visitors who asked for a calmer interface.
  const reducedMotion = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const onRequestVideoRef = useRef(onRequestVideo);
  useEffect(() => {
    onRequestVideoRef.current = onRequestVideo;
  }, [onRequestVideo]);

  // Latest callback in a ref so the auto-advance timer never goes stale.
  const onCycleEndRef = useRef(onCycleEnd);
  useEffect(() => {
    onCycleEndRef.current = onCycleEnd;
  }, [onCycleEnd]);

  // Pause-on-hover only makes sense with a real mouse. On touch devices a
  // tap fires mouseenter without a matching mouseleave, which would freeze
  // the rotation forever — so hover pausing is disabled there.
  const hoverCapable = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover)").matches
  );

  // Keep the active index valid if the slide count changes
  // (e.g. once the CMS data finishes loading).
  useEffect(() => {
    if (current >= slides.length) setCurrent(0);
  }, [slides.length, current]);

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    // Stepping back off the first slide returns to the intro video, closing
    // the loop in both directions.
    if (current === 0 && onRequestVideoRef.current) {
      onRequestVideoRef.current();
      return;
    }
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  }, [current, slides.length]);

  // Touch/drag support — on mobile the arrows are small and swiping is the
  // expected gesture for a carousel.
  const touchStart = useRef(null);
  const handleTouchStart = (e) => {
    touchStart.current = e.touches?.[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStart.current;
    const delta = endX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(delta) < 45) return;
    if (delta < 0) nextSlide();
    else prevSlide();
  };

  useEffect(() => {
    if (paused || slides.length === 0) return undefined;

    const timer = setInterval(() => {
      // Compute outside the state updater so the parent callback is never
      // invoked from React's render phase (double-invoked in StrictMode).
      setCurrent((prev) => {
        if (prev >= slides.length - 1) return prev;
        return prev + 1;
      });
    }, SLIDE_DURATION);

    return () => clearInterval(timer);
  }, [paused, slides.length]);

  // Finished the last slide: hand the hero back to the intro video so the
  // video -> slides -> video loop keeps running.
  useEffect(() => {
    if (paused || slides.length === 0) return undefined;
    if (current < slides.length - 1) return undefined;

    const timer = setTimeout(() => {
      if (onCycleEndRef.current) {
        onCycleEndRef.current();
      } else {
        setCurrent(0);
      }
    }, SLIDE_DURATION);

    return () => clearTimeout(timer);
  }, [current, paused, slides.length]);



  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () =>
      window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  // Guard against a stale index for a single render (e.g. right after
  // the CMS data replaces the fallback slides with a shorter list) and
  // against an empty slide list, which would crash the whole hero.
  const activeSlide = slides[current] || slides[0] || FALLBACK_SLIDES[0];

  return (
    <section
      className={`hero-slider ${activeSlide.isBanner ? "hero-slide--banner" : ""}`}
      onMouseEnter={() => { if (hoverCapable.current) setPaused(true); }}
      onMouseLeave={() => { if (hoverCapable.current) setPaused(false); }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Zetech University highlights"
      data-reduced-motion={reducedMotion.current ? "true" : "false"}
    >
      {/* Background */}

      {/* The slide background is the LCP element on the landing page —
          preload it at high priority so it starts downloading with the
          document instead of after React hydrates. */}
      <Helmet>
        {activeSlide.imageSet ? (
          /* Two typed preloads: browsers ignore the one whose type they
             cannot decode, so AVIF users fetch the smallest file and
             everyone else falls back to WebP. */
          activeSlide.imageSet.avifSrcSet ? (
            <link
              rel="preload"
              as="image"
              type="image/avif"
              href={activeSlide.imageSet.src}
              imageSrcSet={activeSlide.imageSet.avifSrcSet}
              imageSizes={HERO_IMAGE_SIZES}
              fetchPriority="high"
            />
          ) : (
            <link
              rel="preload"
              as="image"
              type="image/webp"
              href={activeSlide.imageSet.src}
              imageSrcSet={activeSlide.imageSet.webpSrcSet}
              imageSizes={HERO_IMAGE_SIZES}
              fetchPriority="high"
            />
          )
        ) : (
          <link
            rel="preload"
            as="image"
            href={activeSlide.image}
            fetchPriority="high"
          />
        )}
      </Helmet>

      <picture>
        {activeSlide.imageSet?.avifSrcSet && (
          <source
            type="image/avif"
            srcSet={activeSlide.imageSet.avifSrcSet}
            sizes={HERO_IMAGE_SIZES}
          />
        )}
        {activeSlide.imageSet && (
          <source
            type="image/webp"
            srcSet={activeSlide.imageSet.webpSrcSet}
            sizes={HERO_IMAGE_SIZES}
          />
        )}
        {activeSlide.imageSet && (
          <source
            type="image/jpeg"
            srcSet={activeSlide.imageSet.jpegSrcSet}
            sizes={HERO_IMAGE_SIZES}
          />
        )}
        <img
          src={activeSlide.image}
          alt={activeSlide.title || "Omoka na Zetech referral programme banner"}
          className="hero-image hero-image--kenburns"
          width={activeSlide.imageSet?.width}
          height={activeSlide.imageSet?.height}
          fetchPriority="high"
          decoding="async"
          loading="eager"
        />
      </picture>

      <div className="hero-overlay">

        <div className="hero-wrapper">

          {/* =========================
              LEFT CONTENT
          ========================== */}

          <div className={`hero-content ${activeSlide.isBanner ? "hero-content--banner" : ""}`}>

            {!activeSlide.isBanner && (
              <>
                <span className="hero-badge">
                  {activeSlide.badge}
                </span>

                <h1>{activeSlide.title}</h1>

                <p>{activeSlide.description}</p>
              </>
            )}

            <div className="hero-buttons">

              <HeroButton
                href={activeSlide.primaryLink}
                className="btn-primary"
                label={activeSlide.primaryButton}
              />

              <HeroButton
                href={activeSlide.secondaryLink}
                className="btn-secondary"
                label={activeSlide.secondaryButton}
              />

            </div>


            {!activeSlide.isBanner && (
              <div className="hero-stats">

                <div>
                  <h2>75K+</h2>
                  <span>Alumni</span>
                </div>

                <div>
                  <h2>4</h2>
                  <span>Campuses</span>
                </div>

                <div>
                  <h2>15+</h2>
                  <span>Nationalities</span>
                </div>

                <div>
                  <h2>25+</h2>
                  <span>Years</span>
                </div>

              </div>
            )}

          </div>

          {/* =========================
              TECHIZEN MASCOT (landing hero only)
          ========================== */}

          {!activeSlide.isBanner && (
            <div className="hero-mascot">
              <Mascot
                message={
                  activeSlide.mascotMessage ||
                  "Hi 👋 I'm Techizen AI.\nClick me to chat!"
                }
                onToggleChat={openTechizenChat}
              />
            </div>
          )}

        </div>

      </div>

      {/* Previous */}

      <button
        className="hero-arrow1 hero-arrow-left1"
        onClick={prevSlide}
        aria-label="Previous Slide"
      >
        <FaArrowLeft />
      </button>

      {/* Next */}

      <button
        className="hero-arrow1 hero-arrow-right1"
        onClick={nextSlide}
        aria-label="Next Slide"
      >
        <FaArrowRight />
      </button>

      {/* Indicators. The first pip returns to the intro video so the whole
          reel — video plus every slide — is reachable in one control. */}

      <div className="hero-indicators" role="tablist" aria-label="Choose a hero slide">

        {onRequestVideo && (
          <button
            type="button"
            className="indicator indicator--video"
            onClick={() => onRequestVideo()}
            aria-label="Play the intro video"
            title="Intro video"
          />
        )}

        {slides.map((slide, index) => (
          <button
            key={`${slide.title || "slide"}-${index}`}
            type="button"
            role="tab"
            aria-selected={current === index}
            className={`indicator ${
              current === index ? "active" : ""
            }`}
            onClick={() => setCurrent(index)}
            aria-label={slide.title ? `Go to slide: ${slide.title}` : `Go to slide ${index + 1}`}
            title={slide.title || `Slide ${index + 1}`}
          >
            {current === index && !paused && (
              <span className="indicator-progress" key={`p-${current}`} />
            )}
          </button>
        ))}

      </div>

      {/* Screen-reader-only status so non-visual users know the hero moved. */}
      <p className="hero-sr-status" aria-live="polite">
        {`Slide ${current + 1} of ${slides.length}${
          activeSlide.title ? `: ${activeSlide.title}` : ""
        }`}
      </p>

    </section>
  );
}